import type {
  FormFieldCustomValidationReport,
  FormFieldSchema,
} from '@gl/lit/components/abstract/form_field/index.js';
import FlyFormField, {
  ActionTag,
  FormFieldValueTypeRepr,
  FORM_FIELD_STATUS,
} from '@gl/lit/components/abstract/form_field/index.js';
import {isNonEmptyString} from '@gl/lit/utils/index.js';
import type {CSSResultArray} from 'lit';
import {html} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import type {CustomChangeEvent as FlyRadioFieldsetChangeEvent} from '@gl/lit/components/base/radio_fieldset/index.js';
import type FlyRadioFieldset from '@gl/lit/components/base/radio_fieldset/index.js';
import {COMPONENT_TAG as FLY_RADIO_FIELDSET_COMPONENT_TAG} from '@gl/lit/components/base/radio_fieldset/index.js';
import styles from './style.scss';
import {ref} from 'lit/directives/ref.js';
import {ImmutableFormRegistry} from '@gl/lit/lib/immutable_form.js';
import type {CustomInputEvent} from '../input/index.js';
import {ifDefined} from 'lit/directives/if-defined.js';

export interface FormRadioFieldOption {
  value: string;
  label: string;
}

export const COMPONENT_TAG = 'fly-form-radio-field';

@customElement(COMPONENT_TAG)
export default class FlyFormRadioField extends FlyFormField {
  @property({type: String})
  name = '';

  @property({type: String})
  label = '';

  @property({type: String})
  hint? = '';

  @property({type: Boolean})
  required = false;

  @property({type: Boolean})
  hideFieldsetLabel = false;

  @property({type: String})
  initial = '';

  @property({type: String})
  value = '';

  @property({type: String})
  width!: string;

  @property({type: String})
  height!: string;

  @property({type: Number})
  col = 3;

  @state()
  schema: FormFieldSchema | null = null;

  @state()
  radioFieldsetValidity: FormFieldCustomValidationReport = {
    state: true,
    message: [],
  };

  $fieldNode!: FlyRadioFieldset;

  static get styles(): CSSResultArray {
    return [styles];
  }

  load() {
    this.schema = {
      type: FormFieldValueTypeRepr.CUSTOM,
      validator: () => {
        return this.radioFieldsetValidity;
      },
    };
  }

  firstUpdated() {
    this.reset();
  }

  reset() {
    super.reset();
    if (!this.$fieldNode) return;
    this.$fieldNode.initialize();
  }

  registerFieldsetNode($node: Element | undefined) {
    if (!$node) return;
    const nodeTagName = $node.tagName.toLowerCase();
    if (nodeTagName !== FLY_RADIO_FIELDSET_COMPONENT_TAG) return;
    this.$fieldNode = <FlyRadioFieldset>$node;
  }

  beforeFormSubmission() {
    if (!this.$fieldNode) return;
    this.$fieldNode.touched = true;
    this.$fieldNode.visited = true;
    this.radioFieldsetValidity = this.$fieldNode.validity;
    super.beforeFormSubmission();
  }

  fieldMarkup() {
    if (
      !this.schema ||
      ![
        FormFieldValueTypeRepr.STRING_ARRAY,
        FormFieldValueTypeRepr.CUSTOM,
      ].includes(this.schema.type)
    ) {
      return html``;
    }

    const classes = {
      form__field: true,
      'form__field--radio-fieldset': true,
      'form__field--active': this.active,
      'form__field--radio-fieldset--active': this.active,
      'form__field--disabled': this.disabled,
      'form__field--radio-fieldset--disabled': this.disabled,
      'form__field--error': this.errors.length > 0,
      'form__field--radio-fieldset--error': this.errors.length > 0,
      'form__field--valid': this.errors.length === 0,
      'form__field--radio-fieldset--valid': this.errors.length === 0,
    };

    const styles = {
      '--fly-radio-fieldset-content-w': isNonEmptyString(this.width)
        ? this.width.trim()
        : '100%',

      '--fly-radio-fieldset-content-h': isNonEmptyString(this.height)
        ? this.height.trim()
        : 'unset',
    };

    return html`
      <div class=${classMap(classes)} style=${styleMap(styles)}>
        <fly-radio-fieldset
          ${ref(this.registerFieldsetNode.bind(this))}
          key=${this.key}
          ?active=${this.active}
          label=${this.label}
          hint=${ifDefined(this.hint)}
          ?visited=${this.visited}
          ?touched=${this.touched}
          name=${isNonEmptyString(this.name) ? this.name : this.key}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          initial=${this.initial}
          ?hideLabel=${this.hideFieldsetLabel}
          col=${this.col}
          @focus=${() => {
            this.action({
              tag: ActionTag.UPDATE_STATUS,
              value: [
                [FORM_FIELD_STATUS.ACTIVE, true],
                [FORM_FIELD_STATUS.VISITED, true],
                [FORM_FIELD_STATUS.TOUCHED, true],
              ],
            });
          }}
          @blur=${() => {
            this.action({
              tag: ActionTag.UPDATE_STATUS,
              value: [
                [FORM_FIELD_STATUS.ACTIVE, false],
                [FORM_FIELD_STATUS.TOUCHED, true],
              ],
            });
          }}
          @change=${(evt: CustomEvent<FlyRadioFieldsetChangeEvent>) => {
            this.action({
              tag: ActionTag.UPDATE_VALUE,
              value: evt.detail.value,
            });

            this.radioFieldsetValidity = evt.detail.validity;
          }}
        >
          <slot></slot>
        </fly-radio-fieldset>
      </div>
    `;
  }
}

// Register immutable field definition to use with forms.
ImmutableFormRegistry.register<FlyFormRadioField, string>(COMPONENT_TAG, {
  getValue(el) {
    return el.value;
  },
  setValue(el, value) {
    el.value = String(value);
  },
  setName(el, name) {
    el.key = String(name);
  },
  reset(el) {
    el.value = '';
  },
  change(el, trigger) {
    el.addEventListener('value', e => {
      const value = (e as CustomEvent<CustomInputEvent>).detail.value;
      trigger(value);
    });
  },
  focus(el, trigger) {
    el.addEventListener('focus', () => trigger());
  },
  blur(el, trigger) {
    el.addEventListener('blur', () => trigger());
  },
});

declare global {
  interface ImmutableFormTypeMap {
    [COMPONENT_TAG]: string;
  }
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyFormRadioField;
  }
}
